import React, { useEffect, useState } from 'react';
import { ArrowLeft, Edit, Ban, RefreshCw, UserCheck, FlaskConical, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { apiService, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { StatusPill } from './AdminModeration';
import { MemberBadges } from './MemberDirectory';
import { OverviewTab, ProfileTab, AccountTab, PremiumTab } from './MemberTabs';
import { SupportTab, ModerationTab, NotesTab, ActivityTab } from './MemberSupportTabs';
import MessagesTab from './MemberMessagesTab';
import UserEditModal from './UserEditModal';
import UserStatusModal from './UserStatusModal';

const TABS = [['overview', 'Overview'], ['profile', 'Profile'], ['account', 'Account'], ['premium', 'Premium'], ['support', 'Support'], ['messages', 'Messages'], ['moderation', 'Moderation'], ['notes', 'Notes'], ['activity', 'Activity']];

const MemberWorkspace = ({ memberId, onBack, onDeleted, initialTab = 'overview' }) => {
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(initialTab);
  const [modal, setModal] = useState(null);
  const { toast } = useToast();
  const fail = (e) => toast({ title: 'Action failed', description: formatApiError(e), variant: 'destructive' });

  const load = () => apiService.admin.getMemberSummary(memberId).then((r) => { setS(r.data.data); setError(''); }).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, [memberId]);

  const act = async (fn, msg) => { try { await fn(); toast({ title: msg }); load(); } catch (e) { fail(e); } };
  const m = s?.member;
  const counts = s ? { support: s.support.open_tickets, moderation: s.moderation.reports_against.filter((r) => ['open', 'reviewing'].includes(r.status)).length, notes: s.notes.length, messages: s.messages.length } : {};

  return (
    <div className="space-y-4" data-testid="member-workspace">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="member-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back to directory</Button>
      {error && <p className="text-red-600" data-testid="member-error">{error}</p>}
      {!s && !error && <Card className="p-8 text-center text-gray-500">Loading member…</Card>}
      {m && (
        <>
          <Card className="p-5 flex flex-wrap items-center gap-4">
            <img src={m.images?.[0] || '/default-avatar.png'} alt="" className="w-16 h-16 rounded-full object-cover bg-gray-100" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2"><h2 className="text-xl font-semibold" data-testid="member-name">{m.name}</h2><StatusPill status={m.status || 'active'} /><MemberBadges m={m} /></div>
              <p className="text-sm text-gray-600" data-testid="member-email">{m.email}</p>
              <p className="text-xs text-gray-400 font-mono">{m.id}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setModal('edit')} data-testid="member-edit-btn"><Edit className="w-4 h-4 mr-1" />Edit profile</Button>
              <Button size="sm" variant="outline" onClick={() => setModal('status')} data-testid="member-status-btn"><Ban className="w-4 h-4 mr-1" />Change status</Button>
              <Button size="sm" variant="outline" onClick={() => act(() => apiService.admin.updateUserPrivileges(m.id, { is_verified: !m.is_verified }), m.is_verified ? 'Verification badge removed' : 'Member marked verified')} data-testid="member-verify-btn"><UserCheck className="w-4 h-4 mr-1" />{m.is_verified ? 'Unverify' : 'Verify'}</Button>
              <Button size="sm" variant="outline" onClick={() => act(() => apiService.admin.updateUserPrivileges(m.id, { is_test_account: !m.is_test_account }), 'QA flag updated')} data-testid="member-qa-btn"><FlaskConical className="w-4 h-4 mr-1" />{m.is_test_account ? 'Unmark QA' : 'Mark QA'}</Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => { if (window.confirm(`Permanently delete ${m.name}? Personal data is purged and this cannot be undone.`)) act(() => apiService.admin.deleteUser(m.id).then(onDeleted), 'Member deleted'); }} data-testid="member-delete-btn"><Trash2 className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={load} data-testid="member-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
            </div>
          </Card>
          <div className="border-b flex gap-1 overflow-x-auto">
            {TABS.map(([id, label]) => (
              <button key={id} type="button" onClick={() => setTab(id)} data-testid={`member-tab-btn-${id}`} className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === id ? 'border-purple-500 text-purple-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                {label}{counts[id] > 0 && <span className="ml-1 text-xs bg-rose-100 text-rose-700 rounded-full px-1.5">{counts[id]}</span>}
              </button>
            ))}
          </div>
          {tab === 'overview' && <OverviewTab s={s} />}
          {tab === 'profile' && <ProfileTab s={s} />}
          {tab === 'account' && <AccountTab s={s} />}
          {tab === 'premium' && <PremiumTab s={s} />}
          {tab === 'support' && <SupportTab s={s} reload={load} />}
          {tab === 'messages' && <MessagesTab s={s} reload={load} />}
          {tab === 'moderation' && <ModerationTab s={s} reload={load} />}
          {tab === 'notes' && <NotesTab s={s} reload={load} />}
          {tab === 'activity' && <ActivityTab s={s} />}
          {modal === 'edit' && <UserEditModal user={m} onClose={() => setModal(null)} onSave={(data) => act(() => apiService.admin.updateUserProfile(m.id, data), 'Profile updated')} />}
          {modal === 'status' && <UserStatusModal user={m} onClose={() => setModal(null)} onSave={(data) => act(() => apiService.admin.updateUserStatus(m.id, data), `Status set to ${data.status}`)} />}
        </>
      )}
    </div>
  );
};

export default MemberWorkspace;
